import { Injectable } from '@angular/core';

import {
  CardViewItemProperties,
  CardViewItem,
  CardViewTextItemModel,
  CardViewBoolItemModel,
  CardViewDateItemModel,
  CardViewDatetimeItemModel,
  CardViewIntItemModel,
  CardViewFloatItemModel,
} from '@alfresco/adf-core';
import {
  CardViewGroup,
  OrganisedPropertyGroup,
  Property,
} from '@alfresco/adf-content-services/lib/content-metadata/interfaces/content-metadata.interfaces';

import { CardViewTableItemModel } from '../components/info-drawer/card-view/models/card-view-table-item.model';
import { WkAlfrescoApplicationService } from './wk-alfresco-application.service';
import { WkAppConfigService } from './wk-app-config.service';

const D_TEXT = 'd:text';
const D_MLTEXT = 'd:mltext';
const D_DATE = 'd:date';
const D_DATETIME = 'd:datetime';
const D_INT = 'd:int';
const D_LONG = 'd:long';
const D_FLOAT = 'd:float';
const D_DOUBLE = 'd:double';
const D_BOOLEAN = 'd:boolean';
const DL_EXTERNALS = 'externals:datalist';

@Injectable({
  providedIn: 'root',
})
export class WkPropertyGroupTranslatorService {
  static readonly RECOGNISED_ECM_TYPES = [
    D_TEXT,
    D_MLTEXT,
    D_DATE,
    D_DATETIME,
    D_INT,
    D_LONG,
    D_FLOAT,
    D_DOUBLE,
    D_BOOLEAN,
    DL_EXTERNALS,
  ];

  valueSeparator: string;

  constructor(
    private readonly wkAppService: WkAlfrescoApplicationService,
    private readonly wkAppConfig: WkAppConfigService
  ) {
    this.valueSeparator = this.wkAppConfig.get<string>(
      'content-metadata.multi-value-pipe-separator'
    );
  }

  public translateToCardViewGroups(
    propertyGroups: OrganisedPropertyGroup[],
    propertyValues
  ): CardViewGroup[] {
    return propertyGroups.map((propertyGroup) => {
      const translatedPropertyGroup: any = Object.assign({}, propertyGroup);
      translatedPropertyGroup.properties = this.translateArray(
        propertyGroup.properties,
        propertyValues
      );
      return translatedPropertyGroup;
    });
  }

  private translateArray(
    properties: Property[],
    propertyValues: any
  ): CardViewItem[] {
    return properties.map((property) => {
      return this.translate(property, propertyValues);
    });
  }

  private translate(property: any, propertyValues: any): CardViewItem {
    let propertyValue;
    if (propertyValues && propertyValues[property.name]) {
      propertyValue = propertyValues[property.name];
    }

    this.checkECMTypeValidity(property.dataType);

    const prefix = 'properties.';

    const propertyDefinition: CardViewItemProperties = {
      label: property.title || property.name,
      value: propertyValue,
      key: `${prefix}${property.name}`,
      default: property.defaultValue,
      editable: 'editable' in property ? property.editable : true,
    };

    let cardViewItemProperty;

    switch (property.dataType) {
      case D_MLTEXT:
        cardViewItemProperty = new CardViewTextItemModel(
          Object.assign(propertyDefinition, {
            multiline: true,
          })
        );
        break;

      case D_INT:
      case D_LONG:
        cardViewItemProperty = new CardViewIntItemModel(propertyDefinition);
        break;

      case D_FLOAT:
      case D_DOUBLE:
        cardViewItemProperty = new CardViewFloatItemModel(propertyDefinition);
        break;

      case D_DATE:
        cardViewItemProperty = new CardViewDateItemModel(propertyDefinition);
        break;

      case D_DATETIME:
        cardViewItemProperty = new CardViewDatetimeItemModel(
          propertyDefinition
        );
        break;

      case D_BOOLEAN:
        cardViewItemProperty = new CardViewBoolItemModel(propertyDefinition);
        break;

      case DL_EXTERNALS:
        cardViewItemProperty = new CardViewTableItemModel(propertyDefinition);
        break;

      case D_TEXT:
      default:
        cardViewItemProperty = new CardViewTextItemModel(
          Object.assign(propertyDefinition, {
            multivalued: property.multiValued,
            multiline: property.multiValued,
          })
        );
    }

    return cardViewItemProperty;
  }

  private checkECMTypeValidity(ecmPropertyType) {
    if (
      WkPropertyGroupTranslatorService.RECOGNISED_ECM_TYPES.indexOf(
        ecmPropertyType
      ) === -1
    ) {
      this.wkAppService.logInstance.error(
        `Unknown type for mapping: ${ecmPropertyType}`
      );
    }
  }
}
